// RITA-1525 (Teilpaket 3) — Platzhalterleiste des Vertragseditors: alle Platzhalter des Vertragskatalogs
// (gemeinsamer Katalog plus eigene Platzhalter der Anschreiben, `model/platzhalterkatalog.ts`) nach Gruppen, mit Suche; ein Klick fügt den Platzhalter an der Schreibmarke ein (auch in
// Kopf- und Fußzeile), markierter Text wird ersetzt. Ohne Schreibmarke sind die Chips gesperrt und ein
// Hinweis erklärt, warum. Die Position (links, rechts, oben als Chips wie im bisherigen Editor) wählt
// der Nutzer; die Probeseite merkt sie sich. Chips verhindern `mousedown`, damit die Auswahl im Editor
// erhalten bleibt (Muster Werkzeugleiste). Seitlich klebt die Leiste unter dem festen App-Kopf
// (APP_KOPF_ABSTAND_PX aus editor/VertragEditor.tsx), sonst läge ihr Kopf beim Scrollen hinter der
// AppBar. Kein Ziehen mit der Maus (Entscheidung Julia 30.09.2026).
// Oben ist die Leiste immer kompakt (RITA-1533, Entscheidung Julia 07.10.2026): Titel, Suchfeld, Hinweis und
// Umschalter in einer Zeile, darunter zwei Chipreihen (eine bei niedrigem Fenster) mit den Gruppennamen klein
// in der Zeile, der Rest per Blättern im Chipbereich oder Suche. Sie klebt unter der Werkzeugleiste; die
// Klebung sitzt am äußeren Anker-Kasten, nicht am Paper (gemessen 08.10.2026). In beiden Lagen stehen dieselben
// Elemente in derselben Reihenfolge, nur die Stilregeln wechseln: der Positionswechsel hängt nichts neu ein.
import React, { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, Paper, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import VerticalSplitOutlinedIcon from '@mui/icons-material/VerticalSplitOutlined';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import { APP_KOPF_ABSTAND_PX } from '../editor/VertragEditor';
import { HEATMAP_LEISTE_PLATZ } from '../../../usage-tracking/heatmap/leistenHoehe';
import { VertragEditorApi } from '../editor/useVertragEditor';
import { platzhalter } from '../model/erzeugen';
import { VERTRAG_PLATZHALTER_GRUPPEN, VertragPlatzhalterKey } from '../model/platzhalterkatalog';
import useFestePlatzhalterleiste from '../layout/useFestePlatzhalterleiste';
import { LEISTEN_ABSTAND_PX } from '../layout/useFesteVertragsleisten';
import {
  CHIP_LUECKE_PX,
  CHIPREIHE_PX,
  kompakteReihen,
  LEISTE_KOPFZEILE_PX,
  Leistenposition,
  LEISTENPOSITIONEN,
  leisteObenHoehePx,
} from '../layout/leistenplatz';

// Lage, Speicherschlüssel und Prüfung liegen in layout/leistenplatz.ts (reine Regeln); hier weiter angeboten.
export {
  LEISTENPOSITIONEN,
  LEISTE_STANDARDPOSITION,
  LEISTE_SPEICHERSCHLUESSEL,
  istLeistenposition,
} from '../layout/leistenplatz';
export type { Leistenposition } from '../layout/leistenplatz';
export const LEISTE_BREITE_PX = 260;
export const LEISTE_TITEL = 'Platzhalter einfügen';
export const LEISTE_HINWEIS_OHNE_SCHREIBMARKE = 'Zum Einfügen zuerst in den Vertragstext klicken.';
export const LEISTE_KEIN_TREFFER = 'Kein Platzhalter gefunden.';
export const LEISTE_SUCHE = 'Platzhalter suchen …';
export const LEISTE_POSITION_BEZEICHNUNG = 'Position der Platzhalterleiste';
/** Hinweis, wenn die seitlich gewählte Leiste wegen des schmalen Rahmens oben steht (V8, Entscheidung Julia 05.10.2026). */
export const LEISTE_HINWEIS_ZU_SCHMAL =
  'Das Fenster ist zu schmal für die Leiste neben dem Vertrag. Sie steht deshalb oben.';

/** Abstand zum festen App-Kopf, derselbe Rand, den der Editor beim Scrollen zur Schreibmarke einhält. */
const KLEBE_ABSTAND_PX = APP_KOPF_ABSTAND_PX + 12;

const POSITIONSNAMEN: Record<Leistenposition, string> = { links: 'links', oben: 'oben', rechts: 'rechts' };

interface Props {
  editor: VertragEditorApi;
  /** Wirksame Lage (V8: bei schmalem Rahmen „oben“, auch wenn seitlich gewählt ist). */
  position: Leistenposition;
  /** Gewählte Lage für Umschalter und Hinweis; ohne Angabe gleich der wirksamen. */
  gewaehlt?: Leistenposition;
  /** Rahmenbreite, ab der die Leiste seitlich Platz hat (nur zur Anzeige am Element). */
  bedarfPx?: number;
  onPositionChange: (position: Leistenposition) => void;
  /** CSS-Reihenfolge im Flex-Rahmen der Seite (die Leiste steht im DOM hinter dem Seitenbereich). */
  reihenfolge?: number;
  gesperrt?: boolean;
  abstandOben?: number;
  /** Fensterhöhe aus der gemeinsamen Leistenmessung; bestimmt oben die Zahl der Chipreihen. */
  fensterHoehe?: number;
  /** Meldet die oben klebende Leiste der gemeinsamen Messung (Schreibrand), siehe useFesteVertragsleisten. */
  onKlebeElement?: (element: HTMLElement | null) => void;
  /** Höhenbudget je fester Leiste (CSS-Ausdruck aus useFesteVertragsleisten); oben begrenzt es die ganze Leiste. */
  maxHoehe?: string;
}

interface Gruppe {
  label: string;
  schluessel: VertragPlatzhalterKey[];
}

const fokusHalten = (event: React.MouseEvent) => event.preventDefault();

interface ChipgruppenProps {
  gruppen: Gruppe[];
  gesperrt: boolean;
  seitlich: boolean;
  onEinfuegen: (schluessel: VertragPlatzhalterKey) => void;
}

/** Chips je Gruppe; als eigener, gemerkter Baustein, damit nicht jeder Tastendruck im Editor alle Chips neu rendert. */
const Chipgruppen = memo(({ gruppen, gesperrt, seitlich, onEinfuegen }: ChipgruppenProps) => (
  <Box
    sx={seitlich ? undefined : { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: `${CHIP_LUECKE_PX}px` }}
  >
    {gruppen.map((gruppe) => (
      // Oben fließen Gruppenname und Chips in einer Zeile: Gruppe und Chipkasten sind dort nur Gliederung ohne
      // eigenen Kasten (display: contents, deshalb ohne messbares Rechteck); seitlich bleibt der Name eine Zeile.
      <Box
        key={gruppe.label}
        sx={seitlich ? { mb: 1.5 } : { display: 'contents' }}
        data-testid="vertrag-platzhaltergruppe"
      >
        <Typography
          variant="caption"
          sx={
            seitlich
              ? { display: 'block', color: 'text.secondary', mb: 0.5 }
              : { color: 'text.secondary', ml: 0.5, whiteSpace: 'nowrap' }
          }
        >
          {gruppe.label}
        </Typography>
        <Box
          sx={seitlich ? { display: 'flex', flexWrap: 'wrap', gap: `${CHIP_LUECKE_PX}px` } : { display: 'contents' }}
        >
          {gruppe.schluessel.map((schluessel) => (
            <Chip
              key={schluessel}
              label={`{{${schluessel}}}`}
              size="small"
              disabled={gesperrt}
              data-platzhalter-chip={schluessel}
              onMouseDown={fokusHalten}
              onClick={() => onEinfuegen(schluessel)}
              sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }}
            />
          ))}
        </Box>
      </Box>
    ))}
  </Box>
));
Chipgruppen.displayName = 'Chipgruppen';

function Platzhalterleiste({
  editor,
  position,
  onPositionChange,
  reihenfolge = 0,
  gesperrt = false,
  abstandOben = KLEBE_ABSTAND_PX,
  fensterHoehe,
  onKlebeElement,
  maxHoehe,
  gewaehlt,
  bedarfPx,
}: Props) {
  const [suche, setSuche] = useState('');
  const wahl = gewaehlt ?? position;
  const erzwungenOben = wahl !== position;
  const ohneSchreibmarke = editor.zustand.auswahl === null;
  const begriff = suche.trim().toLowerCase();
  const gruppen = useMemo<Gruppe[]>(
    () =>
      VERTRAG_PLATZHALTER_GRUPPEN.map((gruppe) => ({
        label: gruppe.label,
        schluessel: [...gruppe.platzhalter].filter(
          (schluessel) =>
            begriff === '' ||
            schluessel.toLowerCase().includes(begriff) ||
            gruppe.label.toLowerCase().includes(begriff),
        ),
      })).filter((gruppe) => gruppe.schluessel.length > 0),
    [begriff],
  );

  // Der Einfüge-Befehl greift immer auf den aktuellen Editor zu, bleibt für die Chips aber dieselbe Funktion.
  const editorRef = useRef(editor);
  editorRef.current = editor;
  const gesperrtRef = useRef(gesperrt);
  gesperrtRef.current = gesperrt;
  const einfuegenRef = useRef((schluessel: VertragPlatzhalterKey) => {
    const aktuell = editorRef.current;
    // Ohne Modell-Auswahl nichts tun (der Chip ist dann gesperrt; jsdom und Tastatur kommen trotzdem hierher).
    if (gesperrtRef.current || aktuell.zustand.auswahl === null) return;
    // Vor dem Befehl die DOM-Auswahl ins Modell übernehmen (selectionchange kommt asynchron).
    aktuell.auswahlLeser.current?.();
    aktuell.befehle.knotenEinfuegen([platzhalter(schluessel)]);
  });

  const seitlich = position !== 'oben';
  const {
    ankerRef,
    leisteRef,
    masse,
    oben,
    hoehe: leistenHoehe,
  } = useFestePlatzhalterleiste(position, seitlich ? abstandOben : abstandOben - LEISTEN_ABSTAND_PX);
  const sichtbareHoehe = `calc(100vh - ${(oben ?? abstandOben) + 16}px - ${HEATMAP_LEISTE_PLATZ})`;
  // Oben zählt die GEMESSENE Kopfzeile (mit umgebrochenen Hinweisen) in die Reihenregel (Kollegenreview !765).
  const kopfzeileRef = useRef<HTMLDivElement>(null);
  const [kopfzeileHoehe, setKopfzeileHoehe] = useState(LEISTE_KOPFZEILE_PX);
  useLayoutEffect(() => {
    const kopfzeile = kopfzeileRef.current;
    if (seitlich || !kopfzeile) return undefined;
    let frame = 0;
    const messen = () => {
      frame = 0;
      if (window.matchMedia?.('print').matches) return;
      const hoehe = Math.round(kopfzeile.getBoundingClientRect().height);
      if (hoehe > 0) setKopfzeileHoehe((alt) => (alt === hoehe ? alt : hoehe));
    };
    const planen = () => {
      if (!frame) frame = window.requestAnimationFrame(messen);
    };
    messen();
    const beobachter = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(planen);
    beobachter?.observe(kopfzeile);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      beobachter?.disconnect();
    };
  }, [seitlich]);
  const reihen = seitlich ? null : kompakteReihen(fensterHoehe, abstandOben - LEISTEN_ABSTAND_PX, kopfzeileHoehe);
  // Passt nicht einmal die kompakte Leiste, scrollt sie als Ganzes. Ein zweiter Scrollbereich
  // würde das Mausrad am inneren Listenende abfangen, während die Chips außen noch abgeschnitten sind.
  const scrolltGanzeLeiste =
    reihen !== null && leistenHoehe !== undefined && leistenHoehe < leisteObenHoehePx(kopfzeileHoehe, reihen) - 1;
  // Oben klebend ist der untere Rand der Leiste der Schreibrand (Scrollen zur Schreibmarke, Pfeiltasten, Mitscrollen).
  useLayoutEffect(() => {
    if (seitlich || !onKlebeElement) return undefined;
    onKlebeElement(leisteRef.current);
    return () => onKlebeElement(null);
  }, [seitlich, onKlebeElement, leisteRef]);

  return (
    <Box
      ref={ankerRef}
      sx={{
        order: reihenfolge,
        ...(seitlich
          ? {
              width: LEISTE_BREITE_PX,
              flex: `0 0 ${LEISTE_BREITE_PX}px`,
              alignSelf: 'flex-start',
              height: masse?.fest ? masse.hoehe : undefined,
            }
          : {
              // Oben klebt die Leiste ohne Spalt direkt an der Unterkante der Werkzeugleiste (Rückmeldung Julia
              // 08.10.2026: durch den früheren 12-px-Spalt schien beim Scrollen das Blatt).
              position: 'sticky',
              top: abstandOben - LEISTEN_ABSTAND_PX,
              zIndex: 9,
            }),
      }}
    >
      <Paper
        ref={leisteRef}
        variant="outlined"
        data-testid="vertrag-platzhalterleiste"
        data-position={position}
        data-gewaehlt={wahl}
        data-bedarf={bedarfPx === undefined ? undefined : Math.ceil(bedarfPx)}
        data-reihen={reihen ?? undefined}
        sx={
          seitlich
            ? {
                width: masse?.fest ? masse.breite : '100%',
                position: masse?.fest ? 'fixed' : 'relative',
                top: masse?.fest ? abstandOben : undefined,
                left: masse?.fest ? masse.links : undefined,
                zIndex: 9,
                maxHeight: sichtbareHoehe,
                overflow: 'auto',
                overscrollBehavior: 'contain',
                p: 1.5,
              }
            : {
                p: 1.5,
                // Die ganze Leiste hält das gemeinsame Höhenbudget der festen Leisten ein (Kollegenreview !765):
                // bei sehr kleinem Fenster blättert sie innen, unter ihr bleibt Platz zum Schreiben.
                maxHeight: maxHoehe ? `min(${maxHoehe}, ${sichtbareHoehe})` : sichtbareHoehe,
                overflowY: 'auto',
                overscrollBehavior: 'contain',
              }
        }
      >
        {/* Eine Kopfzeile für beide Lagen: seitlich fallen Suchfeld und Hinweise auf eigene Zeilen (volle Breite),
            oben steht das Suchfeld zwischen Titel und Umschalter und die Hinweise in einer eigenen Zeile darunter
            (CSS-Reihenfolge; die DOM-Reihenfolge bleibt gleich). */}
        <Box
          ref={kopfzeileRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            columnGap: 1,
            rowGap: 0.5,
            mb: seitlich ? 1 : 0.5,
          }}
        >
          <Typography variant="subtitle2" component="h2" sx={{ flex: seitlich ? '1 1 auto' : '0 0 auto' }}>
            {LEISTE_TITEL}
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={wahl}
            disabled={gesperrt}
            sx={seitlich ? undefined : { order: 3, ml: 'auto' }}
            aria-label={LEISTE_POSITION_BEZEICHNUNG}
            onChange={(_event, wert: Leistenposition | null) => {
              if (!gesperrt && wert) onPositionChange(wert);
            }}
          >
            {LEISTENPOSITIONEN.map((wert) => (
              <ToggleButton key={wert} value={wert} aria-label={`Leiste ${POSITIONSNAMEN[wert]}`} sx={{ px: 0.75 }}>
                <Tooltip title={`Leiste ${POSITIONSNAMEN[wert]}`}>
                  {wert === 'oben' ? (
                    <ViewAgendaOutlinedIcon fontSize="small" />
                  ) : (
                    <VerticalSplitOutlinedIcon
                      fontSize="small"
                      sx={wert === 'rechts' ? { transform: 'scaleX(-1)' } : undefined}
                    />
                  )}
                </Tooltip>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <TextField
            size="small"
            fullWidth={seitlich}
            placeholder={LEISTE_SUCHE}
            inputProps={{ 'aria-label': 'Platzhalter suchen' }}
            value={suche}
            disabled={gesperrt}
            onChange={(event) => setSuche(event.target.value)}
            sx={seitlich ? { flexBasis: '100%' } : { order: 1, flex: '1 1 160px', maxWidth: 320 }}
          />
          {/* Oben ist die Hinweiszeile immer reserviert (auch leer): Sonst spränge beim ersten Klick in den Text die
              Leiste um eine Zeile zusammen und mit ihr das Blatt darunter. Seitlich erscheint sie nur mit Inhalt. */}
          <Box
            data-testid="vertrag-leiste-hinweise"
            sx={
              seitlich
                ? {
                    flexBasis: '100%',
                    display: ohneSchreibmarke || erzwungenOben ? 'flex' : 'none',
                    flexWrap: 'wrap',
                    columnGap: 2,
                    rowGap: 0.5,
                  }
                : {
                    order: 4,
                    flexBasis: '100%',
                    minHeight: 18,
                    display: 'flex',
                    flexWrap: 'wrap',
                    columnGap: 2,
                    rowGap: 0.5,
                  }
            }
          >
            {ohneSchreibmarke && (
              <Typography variant="caption" role="status" sx={{ color: 'text.secondary' }}>
                {LEISTE_HINWEIS_OHNE_SCHREIBMARKE}
              </Typography>
            )}
            {erzwungenOben && (
              <Typography
                variant="caption"
                role="status"
                data-testid="vertrag-leiste-hinweis-schmal"
                sx={{ color: 'text.secondary' }}
              >
                {LEISTE_HINWEIS_ZU_SCHMAL}
              </Typography>
            )}
          </Box>
        </Box>
        {/* Oben begrenzt der Chipbereich sich auf die Reihenzahl und blättert selbst; das Fenster scrollt dabei nicht mit. */}
        <Box
          data-testid="vertrag-platzhalter-chips"
          sx={
            seitlich || reihen === null || scrolltGanzeLeiste
              ? undefined
              : { maxHeight: reihen * CHIPREIHE_PX - CHIP_LUECKE_PX, overflowY: 'auto', overscrollBehavior: 'contain' }
          }
        >
          {gruppen.length === 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {LEISTE_KEIN_TREFFER}
            </Typography>
          )}
          <Chipgruppen
            gruppen={gruppen}
            gesperrt={gesperrt || ohneSchreibmarke}
            seitlich={seitlich}
            onEinfuegen={einfuegenRef.current}
          />
        </Box>
      </Paper>
    </Box>
  );
}

export default Platzhalterleiste;
